/* eslint-disable no-mixed-operators */
import * as React from 'react';
import { Box, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import grey from '@material-ui/core/colors/grey';
import { Redirect, Route, Switch } from 'react-router-dom';

import { Drawer } from './Drawer';
import { SSMPalette } from './AppTheme';
import { AppRouting } from '../AppConstants';
import { Loading } from './Loading';
import { appStore, authService } from '../index';
import { observer } from 'mobx-react';
import { useEffect, useRef } from 'react';

const CompanyTab = React.lazy(() => import('./Tabs/Company/CompanyTab'));
const PlantTab = React.lazy(() => import('./Tabs/Plant/PlantTab'));
const MachineTab = React.lazy(() => import('./Tabs/Machine/MachineTab'));
const AdministrationTab = React.lazy(() => import('./Tabs/Administration/AdministrationTab'));
const RecipeTab = React.lazy(() => import('./Tabs/Recipe/RecipeTab'));

const drawerOpen = 240;
const drawerClosed = 40;
const useStyles = makeStyles((theme) => ({
    main: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
    },
    body: {
        flexGrow: 1,
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
    },
    content: {
        position: 'relative',
        width: `calc(100% - ${drawerClosed}px)`,
        transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        minHeight: 'calc(100vh - 114px)',
    },
    drawer: {
        width: drawerClosed,
        flexShrink: 0,
        background: SSMPalette.gray.sand,
        overflow: 'hidden',
        position: 'relative',
        transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    drawerContainer: {
        width: drawerOpen,
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        background: grey[100],
        overflow: 'auto',
    },
    contentDrawer: {
        width: `calc(100% - ${drawerOpen}px)`,
    },
    drawerOpen: {
        width: drawerOpen,
    },
}));
type LayoutProps = {
    open: boolean;
}
export const Layout = observer((props: LayoutProps) => {
    const classes = useStyles();
    const isAdmin = authService.isAdmin;
    const hasDashboardSubscription = authService.hasDashboardSubscription;
    const hasRecipeManagementAccess = authService.hasRecipeManagementAccess;
    const indexRoute = hasDashboardSubscription && AppRouting.Company ||
        hasRecipeManagementAccess && AppRouting.Recipe ||
        isAdmin && AppRouting.Administration ||
        '/';

    const sidebarRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const _sidebar = sidebarRef.current;
        const _transitionend = (e: TransitionEvent) => {
            if (_sidebar === e.target) {
                window.dispatchEvent(new Event('resize'));
            }
        };
        _sidebar?.addEventListener('transitionend', _transitionend);
        return () => {
            _sidebar?.removeEventListener('transitionend', _transitionend);
        };
    }, []);
    if (!appStore.statusTypes.length) {
        return <Loading isSuspense/>;
    }
    return (
        <Box className={classes.body}>
            <Box {...{ ref: sidebarRef } as any} className={clsx(classes.drawer, { [classes.drawerOpen]: props.open })}
                 boxShadow={4}>
                <Drawer/>
            </Box>
            <Box className={clsx(classes.content, { [classes.contentDrawer]: props.open })}>
                <React.Suspense fallback={<Loading isSuspense/>}>
                    <Switch>
                        {hasDashboardSubscription && <Route path={AppRouting.Company} component={CompanyTab}/>}
                        {hasDashboardSubscription && <Route path={AppRouting.Plant} component={PlantTab}/>}
                        {hasDashboardSubscription && <Route path={AppRouting.Machine} component={MachineTab}/>}
                        {isAdmin && <Route path={AppRouting.Administration} render={() => <AdministrationTab/>}/>}
                        {hasRecipeManagementAccess && <Route path={AppRouting.Recipe} component={RecipeTab}/>}
                        <Redirect exact from="/" to={indexRoute}/>
                        <Redirect from="*" to={indexRoute}/>
                    </Switch>
                </React.Suspense>
            </Box>
        </Box>
    );
});
